import React, { useState } from 'react'
import Base, { components } from 'react-select'
import { useTheme } from 'styled-components'

import { Wrapper, Title, Icon } from './index.styled'
import { mediaMax } from '@dboxdev/ui-components'
import { useScrollSmoother } from '@dboxdev/ui-components/gsap'
import { useAppLayout } from 'layouts/main'

const defaultStyles = ({ colors, calculateResponsiveSize }) => ({
  control: (provided) => ({
    ...provided,
    width: '100%',
    minHeight: '0',
    padding: `${calculateResponsiveSize(20)} ${calculateResponsiveSize(10)} ${calculateResponsiveSize(20)} ${calculateResponsiveSize(20)}`,
    borderRadius: '10px',
    border: `1px solid ${colors.accentColor} !important`,
    background: 'none',
    boxShadow: 'none',
    outline: 'none',
    fontSize: `${calculateResponsiveSize(18)}`,
    lineHeight: '1.1',
    cursor: 'pointer',
    zIndex: '5',
    '.error &': {
      borderColor: 'red !important'
    },
    [mediaMax('mobile')]: {
      padding: `${calculateResponsiveSize(7.5)} ${calculateResponsiveSize(10)}`,
      borderRadius: '4px',
      fontSize: `${calculateResponsiveSize(12)}`
    }
  }),
  dropdownIndicator: () => ({
    zIndex: '-1',
    display: 'flex',
    justifyContent: 'center',
    width: `${calculateResponsiveSize(18)}`,
    padding: '0',
    [mediaMax('mobile')]: {
      width: `${calculateResponsiveSize(14)}`
    }
  }),
  input: (provided) => ({
    ...provided
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: `${calculateResponsiveSize(152)}`,
    padding: `${calculateResponsiveSize(4)} 0`,
    borderRadius: '0',
    border: '0',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: `${colors.accentColor} transparent`,
    '::-webkit-scrollbar': {
      width: '3px'
    },
    '::-webkit-scrollbar-thumb': {
      background: colors.accentColor
    },
    animation: 'selectSlideInOut 0.5s'
  }),
  menu: (provided) => {
    return {
      ...provided,
      height: '0',
      margin: '0',
      borderRadius: '10px',
      border: `1px solid ${colors.accentColor}`,
      borderTop: '0',
      background: colors.backgroundColor,
      boxShadow: 'none',
      visibility: 'hidden',
      overflow: 'hidden',
      transition: 'all 0.5s ease-in-out',
      zIndex: '12',
      [mediaMax('mobile')]: {
        borderRadius: '4px'
      }
    }
  },
  option: (provided) => ({
    ...provided,
    height: `${calculateResponsiveSize(36)}`,
    padding: `0 ${calculateResponsiveSize(20)}`,
    fontSize: `${calculateResponsiveSize(18)}`,
    color: colors.textColor,
    lineHeight: `${calculateResponsiveSize(36)}`,
    borderBottom: 'none',
    background: 'transparent',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: 'none',
      color: colors.accentColor
    },
    '&:active': {
      backgroundColor: 'transparent'
    },
    [mediaMax('mobile')]: {
      height: `${calculateResponsiveSize(30)}`,
      padding: `0 ${calculateResponsiveSize(10)}`,
      fontSize: `${calculateResponsiveSize(12)}`,
      lineHeight: `${calculateResponsiveSize(30)}`
    }
  }),
  placeholder: (provided) => ({
    ...provided,
    margin: '0',
    color: colors.textColor,
    '.error &': {
      color: 'red'
    }
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0',
    alignSelf: 'center',
    input: {
      lineHeight: '1.1'
    }
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    alignSelf: 'center'
  }),
  container: (provided) => ({
    ...provided
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 3
  }),
  singleValue: (provided) => ({
    ...provided,
    margin: '0',
    color: colors.textColor
  })
})

const DropdownIndicator = (props) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <Icon className='select-icon' />
      </components.DropdownIndicator>
    )
  )
}

const Input = ({ title, required = false, className, error, isSearchable = false, ...props }) => {
  const [open, setOpen] = useState(false)
  const smoother = useScrollSmoother()
  const { mobile } = useAppLayout()
  const theme = useTheme()

  const openMenuHandler = async (name) => {
    await setOpen(true)

    const menu = document.querySelector(`#select-${name} .menu`)
    if (menu) {
      smoother.current.paused(true)
      const menuList = menu.firstElementChild
      const maxHeight = Math.round(window.innerWidth * 152 / (mobile ? 393 : 1280))
      const contentHeight = menuList ? menuList.scrollHeight : maxHeight
      menu.style.height = `${Math.min(contentHeight, maxHeight) + 1}px`
      menu.style.visibility = 'visible'

      document.querySelector(`#select-${name} .select-icon`).style.transform =
        'rotate(180deg)'
    }
  }

  const closeMenuHandler = (name) => {
    const menu = document.querySelector(`#select-${name} .menu`)
    if (menu) {
      smoother.current.paused(false)
      menu.style.height = '0'
      menu.style.visibility = 'hidden'

      document.querySelector(`#select-${name} .select-icon`).style.transform =
        'rotate(0deg)'
    }

    setTimeout(() => {
      setOpen(false)
    }, 500)
  }
  //  console.dir(props.name)
  return (
    <Wrapper className={[className].join(' ')}>
      {title && (
        <Title className={error && 'error'}>
          {title}
          {(required || props.rules?.required) && '*'}
        </Title>
      )}
      <Base
        {...props}
        id={`select-${props.name}`}
        isSearchable={isSearchable}
        className={[error && 'error'].join(' ')}
        styles={defaultStyles(theme)}
        menuIsOpen={open}
        menuShouldScrollIntoView={false}
        components={{
          DropdownIndicator,
          Menu: (props) => <components.Menu {...props} className='menu' />
        }}
        onMenuOpen={() => {
          openMenuHandler(props.name)
        }}
        onMenuClose={() => {
          closeMenuHandler(props.name)
        }}
      />
    </Wrapper>
  )
}

export default Input
